import { useCallback, useContext, useMemo, useRef } from 'react';
import { makeStyles, useMediaQuery } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import GlobalLocaleContext from './GlobalLocaleContext';
import { getDisplayForLocale } from '../Helpers/messagesHelpers';
import useRoot from '../RootContext/useRoot';
import { useTheme } from '@mui/material';
import { HeaderBarItem, HeaderBarDropdown, HeaderUtilityBar } from '@ellucian/react-design-system/core';
import Icon from '@ellucian/ds-icons/lib/es6/Icon';

const useStyles = makeStyles({
    name: 'GlobalLocaleHeaderItems'
})(() => ({
    utilityBarBackground: {
        backgroundColor: 'transparent !important',
    },
    themed: {
        '& button': {
            color: 'inherit !important',
        },
        '& button span': {
            color: 'inherit !important',
        },
        '& button svg, & svg': {
            color: 'inherit !important',
            fill: 'currentColor !important',
        },
        '& .ds-icon, & .ds-globe': {
            color: 'inherit !important',
            fill: 'currentColor !important',
        },
    },
}));

const GlobalLocaleHeaderItems = props => {
    const {
        isThemed = false,
        className,
        SearchItem,
        responsiveType = 'none',
    } = props;
    const { formatMessage } = useIntl();
    const { globalTheme } = useRoot();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { classes, cx } = useStyles();
    const globalLanguageCtx = useContext(GlobalLocaleContext);
    const { displayLocale, updateGlobalLocale } = globalLanguageCtx;
    const headerBarItemRef = useRef(null);
    let locales; 
    const tenant = window.PRELOADED_STATE;

    if (tenant.globalSettings.settingMessages.languages) {
        let result = Object.keys(tenant.globalSettings.settingMessages.languages).map(l => ({
            ...tenant.globalSettings.settingMessages.languages[l],
            key: l,
        }));
        if (tenant.globalSettings.disabledLanguages && Array.isArray(tenant.globalSettings.disabledLanguages)) {
            result = result.filter(lang => !tenant.globalSettings.disabledLanguages.includes(lang.key));
        }
        result.sort(function(a,b) {
            let x = a.displayName.toLowerCase();
            let y = b.displayName.toLowerCase();
            return x < y ? -1 : x > y ? 1 : 0;
        });
        locales = result;
    }

    const updateLocale = useCallback((_, item) => { 
        updateGlobalLocale(item.value); 
    }, [updateGlobalLocale]);

    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape' || event.key === 'Esc') {
            // Close the dropdown by removing focus from the button and any open menu
            if (headerBarItemRef.current) {
                const button = headerBarItemRef.current.querySelector('button');
                if (button) {
                    button.blur();
                }
            }
            // Also blur the currently focused element if it's within the dropdown
            if (document.activeElement && headerBarItemRef.current?.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        }
    }, []);

    const iconColor = useMemo(() => {
        return globalTheme?.color4;
    }, [globalTheme]);

    return (
        <>
            { locales && locales.length > 1 &&
                 
                <div ref={headerBarItemRef} onKeyDown={handleKeyDown} role="group" tabIndex={-1}>
                    <HeaderUtilityBar
                        className={cx(classes.utilityBarBackground, className)}
                        isThemed={isThemed}
                        SearchItem={SearchItem}
                        responsiveType={responsiveType}
                    >
                        <HeaderBarItem
                            itemKey="language-utility"
                            className={classes.themed}
                            isUtility
                            responsiveType="none"
                            style={{ color: iconColor }}
                        >
                            <HeaderBarDropdown
                                menuItems={locales?.map(locale => {
                                    const localeDisplayLabel = getDisplayForLocale(locale.key, formatMessage);
                                    return {
                                        key: locale.key,
                                        value: locale.key,
                                        label: localeDisplayLabel
                                    };
                                })
                                }
                                dropdownMenuLabel={<Icon name="globe" />}
                                secondaryDropdownMenuLabel={isMobile ? '' : formatMessage({id: 'globalLocaleLanguages'})}
                                onMenuItemClick={updateLocale}
                                menuHeader={
                                    formatMessage(
                                        {id: 'globalLocaleLanguageSelection'}, 
                                        {language: displayLocale}
                                    )
                                }
                            />
                        </HeaderBarItem>
                    </HeaderUtilityBar>
                </div>
            }
        </>
    );
};
export default GlobalLocaleHeaderItems;
